import type { ColumnConfig } from '@/entities/data-export'
import type { FieldReference, FieldSchemaModel } from '@/shared/generated'
import type { StoredColumnConfig } from '@/shared/tools/columns'
import type { TFunction } from 'i18next'

import { filter, find, isNil, pipe, prop, size, uniqBy } from 'lodash/fp'

import { ValueType } from '@/shared/components/query-builder'
import { formatNumber } from '@/shared/tools/locale'

export const getEnabledColumnsCount = pipe([filter({ enabled: true }), size])

/**
 * Convert schema to config object
 * @param schemas column schema
 * @returns column config object
 */
export function schemaToConfig(
	schemas: FieldSchemaModel[],
	t: TFunction
): ColumnConfig[] {
	return schemas.map((schema) => {
		const name = t(schema.name, { defaultValue: schema.name })

		return {
			key: schema.id,
			name: t(name, { ns: 'fields', defaultValue: name }),
			custom_name: t(name, { ns: 'fields', defaultValue: name }),
			static_value: null,
			type: schema.value_type,
			reference:
				schema.reference && schema.reference.field
					? {
							entity: schema.reference.entity,
							field: schema.reference.field,
						}
					: null,
			enabled: true,
		}
	})
}

/**
 * Create columns configuration used initial and predefined configs
 * *they can have different length
 * @param initialConfig initial column config
 * @param storedConfig stored column config
 * @returns merged column config
 */
export function mergeConfigs(
	initialConfig: ColumnConfig[],
	storedConfig: ColumnConfig[]
) {
	if (initialConfig.length === storedConfig.length) {
		return storedConfig
	} else {
		return uniqBy('key', [
			...storedConfig,
			...initialConfig.map((field) => ({ ...field, enabled: false })),
		])
	}
}

/**
 * Convert stored config to config object
 * @param columnsConfig stored column config
 * @param schemas column schema for getting column name
 * @returns config object
 */
export function storedToConfig(
	columnsConfig: StoredColumnConfig[],
	schemas: FieldSchemaModel[],
	t: TFunction
): ColumnConfig[] {
	return columnsConfig.map((column) => {
		const schema = find({ id: column.key }, schemas)
		const name = schema
			? t(schema.name, { defaultValue: schema.name })
			: column.key

		return {
			key: column.key,
			type: schema?.value_type || ValueType.string,
			name: name,
			custom_name: name,
			static_value: null,
			enabled: column.enabled,
		} as ColumnConfig
	})
}

/**
 * Associate schema type with string
 * @param type data type from schema
 * @returns string representation of the type
 */
export const getSchemaTypeString = (type: string) => {
	switch (type) {
		case ValueType.string:
			return 'String'
		case ValueType.float:
		case ValueType.number:
		case ValueType.integer:
			return 'Number'
		case ValueType.boolean:
			return 'Boolean'
		case ValueType.date:
		case ValueType.datetime:
			return 'DatetimeRange'
		case ValueType.array:
			return 'MultiSelect'
		default:
			return 'NONE'
	}
}

export const createExcelFile = async ({
	name,
	fields,
	rows,
	format,
}: {
	name: string
	fields: ColumnConfig[]
	rows: Record<string, unknown>[]
	format: 'xlsx' | 'csv'
}) => {
	const Excel = await import('exceljs')
	const workbook = new Excel.Workbook()

	workbook.creator = 'Competera'
	workbook.lastModifiedBy = 'Competera'
	workbook.created = new Date()
	workbook.modified = new Date()
	workbook.lastPrinted = new Date()
	const worksheet = workbook.addWorksheet('Sheet1', {
		properties: { defaultColWidth: 15 },
	})
	worksheet.addTable({
		name,
		ref: 'A1',
		headerRow: true,
		style: {
			theme: 'TableStyleMedium1',
		},
		columns: fields.map(({ key, custom_name }) => ({
			name: custom_name,
			key,
		})),
		rows: rows.map((row) =>
			fields.map(({ key, type }) => {
				const value = prop(key, row)
				if (
					[ValueType.number, ValueType.integer, ValueType.float].includes(
						type as ValueType
					)
				) {
					return isNil(value) ? '' : formatNumber(value as number)
				}

				return value as string
			})
		),
	})
	worksheet.getColumn(1).width = 20
	const buffer = await workbook[format].writeBuffer()

	return new Blob([buffer], {
		type: 'application/octet-binary;charset=utf-8',
	})
}

export const getReferenceFields = (ref?: FieldReference | null) => {
	if (
		ref &&
		ref.field &&
		[
			'optimization_group',
			'pricing_campaign',
			'sales_entity',
			'optimization',
			'sales_level',
			'category',
		].includes(ref.entity)
	) {
		return {
			entity: ref.entity,
			field: ref.field,
		}
	}

	return null
}
